import React, { useEffect, useRef, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Box,
  FormHelperText,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";

interface Option {
  label: string;
  value: string | number;
}

interface AsyncMultiAutoCompleteProps {
  label: string;
  value: Option[];
  onChange: (value: Option[]) => void;
  onRegister?: (items: Option[]) => void;
  options: Option[];
  onSearch: (text: string) => void;
  onScrollEnd?: () => void;
  loading?: boolean;
  error?: boolean;
  helperText?: string | null;
  required?: boolean;
  disabled?: boolean;
}

export const AsyncMultiAutoComplete: React.FC<AsyncMultiAutoCompleteProps> = ({
  label,
  value,
  onChange,
  onRegister,
  options,
  onSearch,
  onScrollEnd,
  loading = false,
  error,
  helperText,
  required,
  disabled,
}) => {
  const [search, setSearch] = useState("");

  const onScrollEndRef = useRef(onScrollEnd);
  useEffect(() => { onScrollEndRef.current = onScrollEnd; }, [onScrollEnd]);

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const savedScrollTopRef = useRef<number>(0);
  const isFetchingRef = useRef(false);
  const prevOptionsLengthRef = useRef(0);
  const isRestoringScrollRef = useRef(false);
  const isOpenRef = useRef(false);

  // ─── Debounced search ─────────────────────────────────────────────────────
  useEffect(() => {
    const delay = setTimeout(() => onSearch(search), 400);
    return () => clearTimeout(delay);
  }, [search, onSearch]);

  // ─── Stable scroll handler ────────────────────────────────────────────────
  const handleScroll = useRef((e: Event) => {
    if (isRestoringScrollRef.current) return;

    const target = e.target as HTMLElement;
    savedScrollTopRef.current = target.scrollTop;

    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= 150 && !isFetchingRef.current) {
      isFetchingRef.current = true;
      onScrollEndRef.current?.();
    }
  }).current;

  // ─── Find the MUI menu scroll container ──────────────────────────────────
  const findScrollContainer = (): HTMLElement | null => {
    const candidates = Array.from(
      document.querySelectorAll<HTMLElement>(".MuiMenu-paper, .MuiPopover-paper")
    );
    for (const el of candidates) {
      if (el.scrollHeight > el.clientHeight) return el;
    }
    const listbox = document.querySelector<HTMLElement>("[role='listbox']");
    if (listbox && listbox.scrollHeight > listbox.clientHeight) return listbox;
    return null;
  };

  const attachScrollListener = () => {
    setTimeout(() => {
      const container = findScrollContainer();
      if (container) {
        container.removeEventListener("scroll", handleScroll);
        container.addEventListener("scroll", handleScroll, { passive: true });
        scrollContainerRef.current = container;
      }
    }, 150);
  };

  const detachScrollListener = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.removeEventListener("scroll", handleScroll);
      scrollContainerRef.current = null;
    }
  };

  // ─── Unlock + restore scroll when options grow ────────────────────────────
  useEffect(() => {
    const prevLen = prevOptionsLengthRef.current;
    const newLen = options.length;

    if (newLen > prevLen && isOpenRef.current) {
      const savedTop = savedScrollTopRef.current;
      if (savedTop > 0) {
        isRestoringScrollRef.current = true;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            const container = scrollContainerRef.current ?? findScrollContainer();
            if (container) {
              scrollContainerRef.current = container;
              container.scrollTop = savedTop;
            }
            setTimeout(() => {
              isRestoringScrollRef.current = false;
              isFetchingRef.current = false;
            }, 50);
          })
        );
      } else {
        isFetchingRef.current = false;
      }
    }
    prevOptionsLengthRef.current = newLen;
  }, [options.length]);

  // ─── Menu open / close ────────────────────────────────────────────────────
  const handleMenuOpen = () => {
    isOpenRef.current = true;
    isFetchingRef.current = false;
    isRestoringScrollRef.current = false;
    savedScrollTopRef.current = 0;
    prevOptionsLengthRef.current = options.length;
    attachScrollListener();
  };

  const handleMenuClose = () => {
    isOpenRef.current = false;
    isFetchingRef.current = false;
    isRestoringScrollRef.current = false;
    // Clear search on close so next open shows full list
    setSearch("");
    onSearch("");
    detachScrollListener();
  };

  useEffect(() => () => detachScrollListener(), []);

  // ─── Toggle a single value in the multi-select array ─────────────────────
  const handleToggle = (opt: Option) => {
    const isSelected = value.some((v) => v?.value === opt?.value);

    const next = isSelected
      ? value.filter((v) => v?.value !== opt?.value)
      : [...value, opt];

    onChange(next.filter(Boolean));
    onRegister?.(next.filter(Boolean));
  };

  return (
    <FormControl fullWidth error={error} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>

      <Select
        multiple
        value={value.filter(v => v && v.value !== undefined).map((v) => v.value)}
        input={<OutlinedInput label={label} />}
        disabled={disabled}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        onChange={(e) => {
          const selectedValues = e.target.value;
          const selectedArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
          
          // Map string values back to Option objects
          const newValue = selectedArray
            .map(val => {
              const existing = value.find(v => v && v.value === val);
              if (existing) return existing;
              const newOpt = options.find(o => o && o.value === val);
              return newOpt || { value: val, label: String(val) };
            })
            .filter(v => v && v.value !== undefined);
          
          onChange(newValue);
          onRegister?.(newValue);
        }}
        renderValue={() => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {value
              .filter(opt => opt && opt.label !== undefined)
              .map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onDelete={(e) => {
                  e.stopPropagation();
                  handleToggle(opt);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            ))}
          </Box>
        )}
        MenuProps={{
          disableScrollLock: true,
          // Prevent menu from closing when clicking inside
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: 400,
              overflowY: "auto",
              overflowX: "hidden",
            },
          },
        }}
      >
        {/* Sticky search box */}
        <Box
          p={1}
          position="sticky"
          top={0}
          zIndex={1}
          bgcolor="background.paper"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            placeholder="Search..."
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
          />
        </Box>

        {/* Options */}
        {options
          .filter(opt => opt && opt.value !== undefined && opt.label !== undefined)
          .map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={{ py: 0.5 }}
          >
            <Checkbox
              checked={value.some((v) => v && v.value === opt.value)}
              size="small"
              sx={{ py: 0 }}
            />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}

        {/* Sticky loading indicator */}
        {loading && (
          <MenuItem
            disabled
            sx={{
              position: "sticky",
              bottom: 0,
              bgcolor: "background.paper",
              justifyContent: "center",
              py: 1,
            }}
          >
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Loading more...
          </MenuItem>
        )}

        {!loading && options.length === 0 && (
          <MenuItem disabled>
            <em>No options available</em>
          </MenuItem>
        )}
      </Select>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};